// Space panel — wraps problem or solution tree with header

import { Plus } from 'lucide-react'
import { SpaceNode } from './SpaceNode'
import type { SpaceNode as SpaceNodeType } from '@ispot/shared'
import type { DragSource } from '@/hooks/useDragToLink'

interface SpacePanelProps {
  title: string
  subtitle: string
  space: 'problem' | 'solution'
  nodes: SpaceNodeType[]
  selectedNodeId: string | null
  highlightedIds: Set<string>
  expandedIds: Set<string>
  dropTargetId: string | null
  addLabel: string
  onSelect: (id: string, space: 'problem' | 'solution') => void
  onToggleExpand: (id: string) => void
  onAdd?: () => void
  // Drag-to-link pass-through
  canDrag: (type: string) => boolean
  isValidDropTarget: (type: string, space: 'problem' | 'solution') => boolean
  onDragStart: (source: DragSource, e: React.DragEvent) => void
  onDragOver: (targetId: string, targetType: string, targetSpace: 'problem' | 'solution', e: React.DragEvent) => void
  onDragLeave: () => void
  onDrop: (targetType: string, targetId: string, e: React.DragEvent) => void
  onDragEnd: () => void
}

export function SpacePanel({
  title, subtitle, space, nodes, selectedNodeId, highlightedIds,
  expandedIds, dropTargetId, addLabel, onSelect, onToggleExpand, onAdd,
  canDrag, isValidDropTarget, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
}: SpacePanelProps) {
  const hasSelection = selectedNodeId !== null

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-w-0">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className={`font-display text-[13px] font-semibold uppercase tracking-wide ${space === 'problem' ? 'text-market' : 'text-solution-dark'}`}>
            {title}
          </span>
          <span className="text-[11px] text-text-muted">{subtitle}</span>
        </div>
        {onAdd && (
          <button
            onClick={onAdd}
            className="flex items-center gap-1 text-[12px] text-text-muted border border-dashed border-border rounded-md px-2.5 py-1 hover:text-text-secondary hover:border-warm-400 hover:bg-warm-50 transition-all"
          >
            <Plus size={12} /> {addLabel}
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {nodes.map(node => (
          <SpaceNode
            key={node.id}
            node={node}
            depth={0}
            space={space}
            isSelected={node.id === selectedNodeId}
            isHighlighted={highlightedIds.has(node.id)}
            isDimmed={false}
            isDropTarget={node.id === dropTargetId}
            expandedIds={expandedIds}
            onSelect={onSelect}
            onToggleExpand={onToggleExpand}
            canDrag={canDrag}
            isValidDropTarget={isValidDropTarget}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
          />
        ))}
        {nodes.length === 0 && (
          <div className="text-center text-text-muted text-[13px] py-12">
            No {space === 'problem' ? 'personas' : 'capabilities'} yet
          </div>
        )}
      </div>
    </div>
  )
}
